import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import SecureBaseStore from "./SecureBaseStore";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import UploadStates from "../constants/states/UploadStates";


class ImageUploaderState {
    constructor(
        imageNames,
        uploadingFiles,
        currentShownImageIndex
    ) {
        this.imageNames = imageNames;
        this.uploadingFiles = uploadingFiles;
        this.currentShownImageIndex = currentShownImageIndex;
    }

    currentUploadPercentage() {
        if (this.uploadingFiles.size === 0) {
            return 100;
        }
        let uploadingCount = 0;
        for (const status of this.uploadingFiles.values()) {
            if (status === UploadStates.IDLE || status === UploadStates.UPLOADING) {
                uploadingCount += 1;
            }
        }
        return Math.ceil(((this.uploadingFiles.size - uploadingCount) / this.uploadingFiles.size) * 100);
    }

    equals(other) {
        let diffNames = false;
        if (this.imageNames.length !== other.imageNames.length) {
            diffNames = true;
        } else {
            const otherImageNames = new Set(other.imageNames);
            for (const imageName of this.imageNames) {
                if (!otherImageNames.has(imageName)) {
                    diffNames = true;
                    break;
                }
            }
        }

        let diffUploads = false;
        if (this.uploadingFiles.size !== other.uploadingFiles.size) {
            diffUploads = true;
        } else {
            for (const [imageName, status] of this.uploadingFiles.entries()) {
                if (!other.uploadingFiles.has(imageName) || other.uploadingFiles.get(imageName) !== status) {
                    diffUploads = true;
                    break;
                }
            }
        }

        return this.currentShownImageIndex === other.currentShownImageIndex &&
            !diffNames &&
            !diffUploads;

    }
}

class ImmutableImageUploaderStateBuilder {

    constructor() {
        this.imageNames = [];
        this.uploadingFiles = new Map();
        this.currentShownImageIndex = 0;
    }

    static fromState(state) {
        return new ImmutableImageUploaderStateBuilder()
            .setCurrentShowImageIndex(state.currentShownImageIndex)
            .setImageNames(state.imageNames)
            .setUploadingFiles(state.uploadingFiles);
    }

    setCurrentShowImageIndex(newIndex) {
        this.currentShownImageIndex = newIndex;
        return this;
    }

    setImageNames(imageNames) {
        this.imageNames = imageNames.map((i) => i);
        return this;
    }

    setUploadingFiles(uploadingFiles) {
        this.uploadingFiles = new Map(uploadingFiles);
        return this;
    }

    addImageName(imageName) {
        this.imageNames.push(imageName);
        return this;
    }

    deleteImage(imageName) {
        const index = this.imageNames.indexOf(imageName);
        if (index > -1) {
            let newUrls = this.imageNames.map((i) => (i));
            newUrls.splice(index, 1);
            this.imageNames = newUrls;
            this.currentShownImageIndex = 0;
        }
        return this;
    }

    addUploadingFile(uploadingFile) {
        this.uploadingFiles.set(uploadingFile.name, UploadStates.IDLE);
        return this;
    }

    updateUploadingFileState(uploadingFile, newState) {
        if(this.uploadingFiles.has(uploadingFile.name)) {
            this.uploadingFiles.set(uploadingFile.name, newState);
        }
        return this;
    }

    clearUploadingFiles() {
        this.uploadingFiles = new Map();
        return this;
    }

    build() {
        return Object.freeze(
            new ImageUploaderState(
                this.imageNames,
                this.uploadingFiles,
                this.currentShownImageIndex));
    }
}


class ImageUploaderStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.IMAGE_UPLOADER_STORE_UPDATED, ImmutableImageUploaderStateBuilder);
        this.subscribeToEvent(UIInteractionEvents.FILE_UPLOAD_STARTED, this.handleUploadingFilesStart);
        this.subscribeToEvent(ActionResultEvents.IMAGE_UPLOAD_URL_CREATED, this.handleUploadingFileUploading);
        this.subscribeToEvent(ActionResultEvents.IMAGE_UPLOAD_FINISHED, this.handleUploadingFileFinished);
        this.subscribeToEvent(UIInteractionEvents.SHOWN_IMAGE_CHANGING, this.handleShownImageChanging);
        this.subscribeToEvent(UIInteractionEvents.SHOWN_IMAGE_CHANGE_FINISHED, this.handleShownImageChanged);
        this.subscribeToEvent(UIInteractionEvents.DELETE_IMAGE, this.handleDeleteImage);
        this.subscribeToEvent(ActionResultEvents.ADVERTISEMENT_CREATED, this.resetStore);
        this.subscribeToEvent(ActionResultEvents.SPACE_EDITED, this.resetStore);
        this.subscribeToEvent(ActionResultEvents.SPACE_CREATED, this.resetStore);
        this.subscribeToEvent(UIInteractionEvents.REST_IMAGE_STORE, this.resetStore);
        this.subscribeToEvent(UIInteractionEvents.LOAD_IMAGES_FOR_EDIT, this.handleLoadImages);
    }

    handleDeleteImage = (topic, data) => {
        return ImmutableImageUploaderStateBuilder.fromState(this.state)
            .deleteImage(data.imageName)
            .build();
    };

    handleShownImageChanging = (topic, data) => {
        return ImmutableImageUploaderStateBuilder.fromState(this.state)
            .setCurrentShowImageIndex(-1)
            .build();
    };

    handleShownImageChanged = (topic, data) => {
        return ImmutableImageUploaderStateBuilder.fromState(this.state)
            .setCurrentShowImageIndex(data.toIndex)
            .build();
    };

    handleUploadingFileFinished = (topic, data) => {
        const newState =
            ImmutableImageUploaderStateBuilder.fromState(this.state)
                .updateUploadingFileState(data.image, UploadStates.FINISHED_UPLOAD)
                .addImageName(data.imageName)
                .build();
        if (newState.currentUploadPercentage() === 100) {
            return ImmutableImageUploaderStateBuilder.fromState(newState)
                .clearUploadingFiles()
                .build();
        }
        return newState;
    };

    handleUploadingFileUploading = (topic, data) => {
        return ImmutableImageUploaderStateBuilder.fromState(this.state)
            .updateUploadingFileState(data.image, UploadStates.UPLOADING)
            .build()
    };

    handleUploadingFilesStart = (topic, data) => {
        const builder = ImmutableImageUploaderStateBuilder.fromState(this.state);
        for (let i = 0; i < data.files.length ; i++) {
            const image = data.files.item(i);
            builder.addUploadingFile(image);
        }
        return builder.build();
    };

    handleLoadImages = (topic, data) => {
        return ImmutableImageUploaderStateBuilder.fromState(this.state)
            .setImageNames(data.imageNames)
            .build();
    };
}

export default new ImageUploaderStore();